import { RefObject, useEffect } from "react";

// Workaround for the missing CSS "stuck" state
// (https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/At-rules/@container#stuck):
// a shared rAF-throttled scroll listener per container toggles an attribute on
// each registered sticky element, so styling reacts to scroll without any
// React re-render.

const kStuckAttribute = "data-sticky-stuck";

interface Registry {
  /** Registered sticky elements → their pin offset (px) in the container. */
  entries: Map<Element, number>;
  /** rAF-throttled full-registry re-measure. */
  scheduleUpdate: () => void;
  cleanup: () => void;
}

const registries = new Map<Element, Registry>();

function updateElementStuckState(
  containerTop: number,
  el: Element,
  stickyTop: number
) {
  const relativeTop = el.getBoundingClientRect().top - containerTop;
  // Stuck = pinned exactly at its sticky offset (±1px). Above the band means
  // the element is being pushed off by the next sticky header; below means it
  // is still in normal flow.
  const isStuck = relativeTop <= stickyTop + 1 && relativeTop >= stickyTop - 1;
  el.toggleAttribute(kStuckAttribute, isStuck);
}

function updateStuckState(container: Element, entries: Map<Element, number>) {
  const containerTop = container.getBoundingClientRect().top;
  entries.forEach((stickyTop, el) =>
    updateElementStuckState(containerTop, el, stickyTop)
  );
}

function getRegistry(container: Element): Registry {
  let registry = registries.get(container);
  if (!registry) {
    const entries = new Map<Element, number>();
    let rafId: number | null = null;
    const scheduleUpdate = () => {
      if (rafId === null) {
        rafId = requestAnimationFrame(() => {
          rafId = null;
          updateStuckState(container, entries);
        });
      }
    };
    container.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate, { passive: true });
    registry = {
      entries,
      scheduleUpdate,
      cleanup: () => {
        container.removeEventListener("scroll", scheduleUpdate);
        window.removeEventListener("resize", scheduleUpdate);
        if (rafId !== null) cancelAnimationFrame(rafId);
      },
    };
    registries.set(container, registry);
  }
  return registry;
}

/**
 * Toggles `data-sticky-stuck` on `ref`'s element while it is pinned at
 * `stickyTop` px inside `scrollRef`'s scroll container. Style the stuck state
 * with `[data-sticky-stuck]` attribute selectors. No-op without a container.
 */
export function useStickyStuck(
  ref: RefObject<HTMLElement | null>,
  stickyTop: number,
  scrollRef: RefObject<HTMLElement | null> | undefined
): void {
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    const el = ref.current;
    const container = scrollRef?.current;
    if (!el || !container) return;
    const registry = getRegistry(container);
    registry.entries.set(el, stickyTop);
    // Measure only the new row immediately. Measuring the full registry for
    // every mounted row makes initial render quadratic on long transcripts.
    updateElementStuckState(
      container.getBoundingClientRect().top,
      el,
      stickyTop
    );
    // Re-measure the full registry on the next frame to pick up this mount.
    registry.scheduleUpdate();
    return () => {
      registry.entries.delete(el);
      el.removeAttribute(kStuckAttribute);
      if (registry.entries.size === 0) {
        registry.cleanup();
        registries.delete(container);
      }
    };
  }, [ref, stickyTop, scrollRef]);
}
